// src/components/Sidebar.jsx
import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

/* ================================================================== */
/* Constants                                                           */
/* ================================================================== */

const PINNED_KEY = 'iiee:sidebar:pinned';
const MOBILE_BREAKPOINT = 1024; // lg

/* ================================================================== */
/* Reference-counted body scroll lock (shared with Navbar)             */
/* ================================================================== */

let bodyScrollLockCount = 0;
const lockBodyScroll = () => {
  if (typeof document === 'undefined') return;
  if (bodyScrollLockCount === 0) document.body.style.overflow = 'hidden';
  bodyScrollLockCount += 1;
};
const unlockBodyScroll = () => {
  if (typeof document === 'undefined') return;
  bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
  if (bodyScrollLockCount === 0) document.body.style.overflow = '';
};

/* ================================================================== */
/* Sidebar                                                             */
/* ================================================================== */

const Sidebar = ({
  mobileOpen = false,
  onMobileClose = () => {},
  unreadMessages = 0,
  unreadNotifications = 0,
}) => {
  const { user, isAdmin, isBoard, isApproved, userData } = useAuth();
  const location = useLocation();

  const [pinned, setPinned] = useState(() => {
    try {
      return localStorage.getItem(PINNED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [hovered, setHovered] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= MOBILE_BREAKPOINT,
  );

  const scrollRef = useRef(null);
  const [showScrollFade, setShowScrollFade] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(PINNED_KEY, pinned ? '1' : '0');
    } catch {
      /* ignore quota / private mode */
    }
  }, [pinned]);

  /* Track viewport — debounced + orientation-aware */
  useEffect(() => {
    let t = null;
    const update = () => {
      setIsDesktop(window.innerWidth >= MOBILE_BREAKPOINT);
    };
    const onResize = () => {
      if (t) clearTimeout(t);
      t = setTimeout(update, 80);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', update);
    return () => {
      if (t) clearTimeout(t);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  useEffect(() => {
    if (mobileOpen) onMobileClose();
  }, [location.pathname]); 

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onMobileClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen, onMobileClose]);

  useEffect(() => {
    if (!mobileOpen || isDesktop) return undefined;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [mobileOpen, isDesktop]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasMore = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
    setShowScrollFade(hasMore);
  }, []);

  useEffect(() => {
    handleScroll();
  }, [handleScroll, location.pathname, mobileOpen, unreadMessages, unreadNotifications]);

  const isDrawer = !isDesktop;
  const expanded = isDrawer || (isDesktop && (pinned || hovered));

  const isActive = useCallback(
    (to, exact = false) => {
      const cleanPath = to.split('#')[0];
      return exact
        ? location.pathname === cleanPath
        : location.pathname === cleanPath ||
            location.pathname.startsWith(`${cleanPath}/`);
    },
    [location.pathname],
  );

  const isUserApproved =
    isApproved === true ||
    userData?.approved === true ||
    userData?.isApproved === true ||
    userData?.registrationStatus === 'approved' ||
    isAdmin ||
    isBoard;

  const content = (
    <>
      <nav
        ref={scrollRef}
        onScroll={handleScroll}
        className="p-1.5 space-y-0.5 flex-1 overflow-y-auto overflow-x-hidden"
      >
        {expanded && isDesktop && (
          <div className="flex justify-end pb-0.5">
            <button
              type="button"
              onClick={() => setPinned((p) => !p)}
              aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
              aria-pressed={pinned}
              title={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
              className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                pinned
                  ? 'bg-orange/10 text-orange'
                  : 'text-gray-400 hover:text-orange hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <i
                className={`fas fa-thumbtack text-[10px] ${
                  pinned ? '' : 'rotate-45'
                }`}
                aria-hidden="true"
              />
            </button>
          </div>
        )}

        <SideItem
          to="/"
          icon={<IconWrap color="text-blue-500" icon="fa-home" />}
          label="Home"
          active={isActive('/', true)}
          expanded={expanded}
        />

        {user && isUserApproved && (
          <SideItem
            to="/media"
            icon={<IconWrap color="text-pink-500" icon="fa-images" />}
            label="Media"
            active={isActive('/media', true)}
            expanded={expanded}
          />
        )}

        {user && isUserApproved && (
          <SideItem
            to="/members"
            icon={<IconWrap color="text-cyan-500" icon="fa-user-group" />}
            label="Members"
            active={isActive('/members', true)}
            expanded={expanded}
          />
        )}

        <SideItem
          to="/board"
          icon={<IconWrap color="text-purple-500" icon="fa-users" />}
          label="Board"
          active={isActive('/board', true)}
          expanded={expanded}
        />

        <SideItem
          to="/toolkit"
          icon={<IconWrap color="text-amber-500" icon="fa-calculator" />}
          label="EE Toolkit"
          active={isActive('/toolkit', true)}
          expanded={expanded}
        />

        <SideItem
          to="/contact"
          icon={<IconWrap color="text-teal-500" icon="fa-envelope-open-text" />}
          label="Contact"
          active={isActive('/contact', true)}
          expanded={expanded}
        />

        <SideItem
          to="/about"
          icon={<IconWrap color="text-yellow-500" icon="fa-circle-info" />}
          label="About"
          active={isActive('/about', true)}
          expanded={expanded}
        />

        {user && isUserApproved && (
          <>
            <SideItem
              to="/chat"
              icon={<IconWrap color="text-green-500" icon="fa-comment-dots" />}
              label="Messages"
              active={isActive('/chat', true)}
              expanded={expanded}
              badge={unreadMessages}
              badgeColor="bg-red-500"
            />
            <SideItem
              to="/notifications"
              icon={<IconWrap color="text-red-500" icon="fa-bell" />}
              label="Notifications"
              active={isActive('/notifications', true)}
              expanded={expanded}
              badge={unreadNotifications}
              badgeColor="bg-orange"
            />
          </>
        )}

        {user && isAdmin && (
          <>
            <SectionDivider expanded={expanded} label="Admin" />
            <SideItem
              to="/admin"
              icon={<IconWrap color="text-orange" icon="fa-shield-halved" />}
              label="Admin Panel"
              active={isActive('/admin', true)}
              expanded={expanded}
            />
          </>
        )}

        {user && (
          <>
            <SectionDivider expanded={expanded} label="Account" />
            <SideItem
              to="/profile"
              icon={<IconWrap color="text-indigo-500" icon="fa-user" />}
              label="Profile"
              active={isActive('/profile', true)}
              expanded={expanded}
            />
            <SideItem
              to="/settings"
              icon={<IconWrap color="text-gray-500" icon="fa-cog" />}
              label="Settings"
              active={isActive('/settings', true)}
              expanded={expanded}
            />
          </>
        )}

        {!user && (
          <div className="pt-2 mt-1 border-t border-gray-200 dark:border-gray-700 space-y-1">
            {expanded ? (
              <>
                <p className="px-2 text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                  Sign in to unlock messaging and notifications.
                </p>
                <Link
                  to="/login"
                  className="block text-center bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100 py-1.5 rounded-md text-xs font-medium transition active:scale-[0.98]"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="block text-center bg-orange hover:bg-orange-dark text-white py-1.5 rounded-md text-xs font-medium transition active:scale-[0.98]"
                >
                  Create Account
                </Link>
              </>
            ) : (
              <Link
                to="/login"
                aria-label="Sign In"
                title="Sign In"
                className="flex items-center justify-center w-8 h-8 mx-auto rounded-full bg-orange text-white hover:bg-orange-dark transition-colors"
              >
                <i className="fas fa-sign-in-alt text-xs" aria-hidden="true" />
              </Link>
            )}
          </div>
        )}
      </nav>

      <div
        aria-hidden="true"
        className={`pointer-events-none absolute bottom-0 left-0 right-0 h-6 transition-opacity bg-gradient-to-t from-white dark:from-gray-900 to-transparent ${
          showScrollFade ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {expanded && (
        <div className="flex-shrink-0 px-2 py-2 text-[10px] text-gray-400 dark:text-gray-500 flex flex-wrap gap-x-1.5 gap-y-0.5 border-t border-gray-100 dark:border-gray-800">
          <Link to="/settings" className="hover:text-orange">
            Settings
          </Link>
          <span>·</span>
          <Link to="/board" className="hover:text-orange">
            Board
          </Link>
          <span>·</span>
          <Link to="/toolkit" className="hover:text-orange">
            EE Toolkit
          </Link>
          <span>·</span>
          <Link to="/tools" className="hover:text-orange">
            Tools
          </Link>
          <span>·</span>
          <Link to="/contact" className="hover:text-orange">
            Contact
          </Link>
          <span>·</span>
          <Link to="/about" className="hover:text-orange">
            About
          </Link>
          {user && isUserApproved && (
            <>
              <span>·</span>
              <Link to="/media" className="hover:text-orange">
                Media
              </Link>
              <span>·</span>
              <Link to="/members" className="hover:text-orange">
                Members
              </Link>
            </>
          )}
        </div>
      )}
    </>
  );

  if (isDrawer) {
    return (
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm lg:hidden"
              onClick={onMobileClose}
              aria-hidden="true"
            />

            <motion.aside
              key="drawer"
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              className="fixed top-0 bottom-0 left-0 z-[61] w-64 max-w-[80vw] flex flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 lg:hidden shadow-2xl"
              role="dialog"
              aria-modal="true"
              aria-label="Main navigation"
            >
              <div className="flex items-center justify-between px-3 py-3 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
                <Link
                  to="/"
                  className="flex items-center gap-2"
                  onClick={onMobileClose}
                >
                  <img
                    src="/images/iiee-logo.png"
                    alt="IIEE-ERCSA"
                    className="w-8 h-8 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <span className="text-sm font-bold text-gray-800 dark:text-white">
                    IIEE-ERCSA
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={onMobileClose}
                  aria-label="Close navigation"
                  className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>

              <div className="relative flex-1 flex flex-col overflow-hidden">
                {content}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    );
  }

  return (
    <aside
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`hidden lg:flex relative flex-col flex-shrink-0
                  border-r border-gray-200 dark:border-gray-700
                  bg-white dark:bg-gray-900
                  sticky top-16 h-[calc(100vh-4rem)]
                  transition-[width] duration-300 ease-in-out
                  ${expanded ? 'w-56' : 'w-14'}`}
      aria-label="Main navigation"
    >
      {content}
    </aside>
  );
};

/* ================================================================== */
/* SideItem                                                            */
/* ================================================================== */

const SideItem = ({
  to,
  icon,
  label,
  active,
  expanded,
  badge = 0,
  badgeColor = 'bg-orange',
}) => {
  const hasBadge = badge > 0;
  const displayBadge = badge > 99 ? '99+' : String(badge);

  return (
    <Link
      to={to}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={`relative flex items-center gap-2.5 p-1 rounded-lg transition-colors outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-900 ${
        active
          ? 'bg-orange/10 text-orange'
          : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'
      } ${expanded ? '' : 'justify-center'}`}
      title={!expanded ? label : undefined}
    >
      <div className="relative flex items-center justify-center flex-shrink-0 w-8 h-8">
        {icon}

        {active && (
          <motion.span
            layoutId="sidebar-active"
            className="absolute inset-0 rounded-full ring-2 ring-orange/40"
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            aria-hidden="true"
          />
        )}

        {!expanded && hasBadge && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full ${badgeColor} text-white text-[9px] font-bold flex items-center justify-center shadow-sm`}
            aria-label={`${badge} unread`}
          >
            {displayBadge}
          </span>
        )}
      </div>

      {expanded && (
        <>
          <span className="text-sm font-medium truncate flex-1">{label}</span>
          {hasBadge && (
            <span
              className={`flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full ${badgeColor} text-white text-[10px] font-bold flex items-center justify-center shadow-sm`}
              aria-label={`${badge} unread`}
            >
              {displayBadge}
            </span>
          )}
        </>
      )}
    </Link>
  );
};

/* ================================================================== */
/* IconWrap                                                            */
/* ================================================================== */

const IconWrap = ({ color, icon }) => (
  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
    <i className={`fas ${icon} ${color} text-sm`} aria-hidden="true" />
  </div>
);

/* ================================================================== */
/* SectionDivider                                                      */
/* ================================================================== */

const SectionDivider = ({ expanded, label }) => (
  <div className="pt-2 mt-1.5 border-t border-gray-200 dark:border-gray-700">
    {expanded && (
      <p className="px-2 pt-1 pb-0.5 text-[10px] uppercase font-semibold text-gray-400 dark:text-gray-500 tracking-wider">
        {label}
      </p>
    )}
  </div>
);

export default Sidebar;